import { ChangeEvent, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { addSaleDepositSlips } from "../../services";
import { SaleDepositSlipMetadata } from "../../types/sale-deposit.type";

const API_DATE_FORMAT = "YYYY-MM-DD";
const TIME_FORMAT = "HH:mm";
/** Matches the server's per-deposit slip cap. */
const MAX_SLIPS = 20;
const IMAGE_ACCEPT = "image/png,image/jpeg,image/webp";

interface PendingSlip {
  file: File;
  amount: string;
  date: Dayjs;
  time: string;
}

interface AddSlipsDialogProps {
  open: boolean;
  depositUuid: string | null;
  /** Slips already on the deposit — the cap counts both. */
  existingSlipCount: number;
  onClose: () => void;
  /** Called after a successful upload so the list picks up the new total. */
  onUploaded: () => void;
}

const toPendingSlip = (file: File): PendingSlip => ({
  file,
  amount: "",
  date: dayjs().startOf("day"),
  time: dayjs().format(TIME_FORMAT),
});

const isComplete = (slip: PendingSlip): boolean =>
  Number(slip.amount) > 0 && slip.date.isValid() && /^\d{1,2}:\d{2}$/.test(slip.time);

const toMetadata = (slip: PendingSlip): SaleDepositSlipMetadata => ({
  transfer_amount: Number(slip.amount),
  transfer_date: slip.date.format(API_DATE_FORMAT),
  transfer_time: slip.time,
});

/**
 * BO attaching the slips a salesperson never sent.
 *
 * Each image carries the amount, date and time read off the slip itself, exactly as the
 * salesperson would have entered them in the field app — the deposit's total is re-derived
 * from the set, so a wrong figure here moves ยอดโอน. Slips cannot be removed once saved,
 * which is why the list is editable up to the moment it is sent and not after.
 */
export default function AddSlipsDialog({
  open,
  depositUuid,
  existingSlipCount,
  onClose,
  onUploaded,
}: AddSlipsDialogProps) {
  const [slips, setSlips] = useState<PendingSlip[]>([]);
  const [note, setNote] = useState<string>("");
  const [saving, setSaving] = useState<boolean>(false);

  const remainingSlots = MAX_SLIPS - existingSlipCount - slips.length;
  const canSave =
    !saving && slips.length > 0 && slips.every(isComplete) && remainingSlots >= 0;

  const handleSelectFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const picked = Array.from(event.target.files ?? []);
    // Reset the input so re-picking the same file still fires a change event.
    event.target.value = "";
    if (picked.length === 0) return;
    setSlips((prev) => [...prev, ...picked.map(toPendingSlip)]);
  };

  const handleRemove = (index: number) => () => {
    setSlips((prev) => prev.filter((_, position) => position !== index));
  };

  const handleChangeAmount =
    (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setSlips((prev) =>
        prev.map((slip, position) =>
          position === index ? { ...slip, amount: value } : slip,
        ),
      );
    };

  const handleChangeTime =
    (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const { value } = event.target;
      setSlips((prev) =>
        prev.map((slip, position) =>
          position === index ? { ...slip, time: value } : slip,
        ),
      );
    };

  const handleChangeDate = (index: number) => (value: Dayjs | null) => {
    if (!value) return;
    setSlips((prev) =>
      prev.map((slip, position) =>
        position === index ? { ...slip, date: value } : slip,
      ),
    );
  };

  const handleChangeNote = (event: ChangeEvent<HTMLInputElement>) => {
    setNote(event.target.value);
  };

  const handleClose = () => {
    if (saving) return;
    setSlips([]);
    setNote("");
    onClose();
  };

  const handleSave = async () => {
    if (!depositUuid || !canSave) return;
    setSaving(true);
    try {
      await addSaleDepositSlips(
        depositUuid,
        slips.map((slip) => ({ file: slip.file, metadata: toMetadata(slip) })),
        note.trim() ? note.trim() : null,
      );
      setSlips([]);
      setNote("");
      onUploaded();
      onClose();
    } catch {
      // The axios interceptor surfaces the message; keep the form so nothing is retyped.
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>แนบสลิปแทนพนักงานขาย</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Typography variant="body2" color="text.secondary">
            แนบได้อีก {Math.max(remainingSlots, 0)} ใบ · สลิปที่บันทึกแล้วจะลบหรือแก้ไขรูปไม่ได้
          </Typography>

          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileIcon />}
            disabled={saving || remainingSlots <= 0}
            sx={{ alignSelf: "flex-start" }}
          >
            เลือกไฟล์สลิป
            <input
              hidden
              multiple
              type="file"
              accept={IMAGE_ACCEPT}
              onChange={handleSelectFiles}
            />
          </Button>

          {slips.map((slip, index) => (
            <Paper key={`${slip.file.name}-${index}`} variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={1.5}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" noWrap sx={{ flex: 1, minWidth: 0 }}>
                    {slip.file.name}
                  </Typography>
                  <IconButton
                    size="small"
                    disabled={saving}
                    onClick={handleRemove(index)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </Stack>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}>
                  <TextField
                    size="small"
                    fullWidth
                    label="ยอดโอน"
                    type="number"
                    value={slip.amount}
                    onChange={handleChangeAmount(index)}
                    disabled={saving}
                  />
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="วันที่โอน"
                      value={slip.date}
                      format="DD/MM/YYYY"
                      disabled={saving}
                      onChange={handleChangeDate(index)}
                      slotProps={{ textField: { size: "small", fullWidth: true } }}
                    />
                  </LocalizationProvider>
                  <TextField
                    size="small"
                    fullWidth
                    label="เวลาโอน"
                    type="time"
                    value={slip.time}
                    onChange={handleChangeTime(index)}
                    disabled={saving}
                  />
                </Stack>
              </Stack>
            </Paper>
          ))}

          <TextField
            size="small"
            fullWidth
            multiline
            minRows={2}
            label="หมายเหตุ (ไม่บังคับ)"
            value={note}
            onChange={handleChangeNote}
            disabled={saving}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button color="inherit" onClick={handleClose} disabled={saving}>
          ยกเลิก
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={!canSave}>
          บันทึก
        </Button>
      </DialogActions>
    </Dialog>
  );
}
